import { FC } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { Home } from "../../home/components/Home";
import { TaskSubmission } from "../../task-submission/components/TaskSubmission";
import { CreateNewTask } from "../../task-submission/components/CreateNewTask";
import { UserManagement } from "../../user-management/components/UserManagement";
import { CreateNewUser } from "../../user-management/components/CreateNewUser";
import { UpdateUserDetail } from "../../user-management/components/UpdateUserDetail";
import { CompanyManagement } from "../../company-management/components/CompanyManagement";
import { CreateNewCompany } from "../../organization-management/components/CreateNewCompany";
import { UpdateCompanyInfo } from "../../organization-management/components/UpdateCompanyInfo";
import { CreateParentCompany } from "../../company-management/components/CreateParentCompany";
import { UpdateParentCompany } from "../../company-management/components/UpdateParentCompany";
import { CreateNewJobTitle } from "../../jobs-management/components/CreateNewJobTitle";
import { UpdateJobTitle } from "../../jobs-management/components/UpdateJobTitle";
import { TasksListManagement } from "../../tasks-list-management/components/TasksListManagement";
import { CreateNewTemplate } from "../../tasks-list-management/components/CreateNewTemplate";
import { UpdateTemplateDetail } from "../../tasks-list-management/components/UpdateTemplateDetail";
import { TaskCounterList } from "../../task-counter/components/TaskCounterList";
import { CreateTaskCounterTemplate } from "../../task-counter/components/CreateTaskCounterTemplate";
import { UpdateTaskCounterTemplate } from "../../task-counter/components/UpdateTaskCounterTemplate";
import { TaskCounterSubmissionList } from "../../task-counter/components/TaskCounterSubmissionList";
import { TaskCounterScreen } from "../../task-counter/components/TaskCounterScreen";
import { UpdateTaskSubmission } from "../../task-submission/components/UpdateTaskSubmission";
import { PageNotFound } from "../../home/components/PageNotFound";
import { isExistsLogin } from "../../common/miscellaneous/handleCookies";
import { PreAccess } from "./PreAccess";

export const AppRoutes: FC = () => {
  const userRole = !!localStorage.getItem("rolesJson")
    ? JSON.parse(localStorage.getItem("rolesJson") as string)
    : [];

  const isRoleAdmin =
    userRole &&
    userRole.length > 0 &&
    userRole[0]?.Name &&
    userRole[0]?.Name.toLowerCase() === "admin";

  const isRoleSupperAdmin =
    userRole &&
    userRole.length > 0 &&
    userRole[0]?.Name &&
    userRole[0]?.Name.toLowerCase() === "super admin";

  const participantType = localStorage.getItem("participantType");
  const isOpsWorkLog = participantType === "2";
  const isTimeStudy = participantType === "1";

  // Determine the correct home route based on participant type
  const homeRoute = isExistsLogin()
    ? isOpsWorkLog
      ? "/task-counter-submission"
      : "/task-submission"
    : undefined;

  return (
    <Routes>
      {/* Redirect logged-in users to their correct module home */}
      <Route
        path="/"
        element={homeRoute ? <Navigate to={homeRoute} replace /> : <Home />}
      />
      <Route path="/task-submission/*" element={<PreAccess />} />

      {isExistsLogin() ? (
        <>
          {/* Admin/Super Admin can access both Time Study and Task Counter */}
          {isRoleAdmin || isRoleSupperAdmin ? (
            <>
              <Route path="/task-submission" element={<TaskSubmission />} />
              <Route path="/create-task" element={<CreateNewTask />} />
              <Route path="/update-task/:taskId" element={<UpdateTaskSubmission />} />
              <Route path="/task-counter-submission" element={<TaskCounterSubmissionList />} />
              <Route path="/task-counter-screen" element={<TaskCounterScreen />} />
            </>
          ) : (
            <>
              {/* Time Study routes — blocked for Ops Work Log users */}
              {isOpsWorkLog ? (
                <>
                  <Route path="/task-submission" element={<Navigate to="/task-counter-submission" replace />} />
                  <Route path="/create-task" element={<Navigate to="/task-counter-submission" replace />} />
                  <Route path="/update-task/:taskId" element={<Navigate to="/task-counter-submission" replace />} />
                </>
              ) : (
                <>
                  <Route path="/task-submission" element={<TaskSubmission />} />
                  <Route path="/create-task" element={<CreateNewTask />} />
                  <Route path="/update-task/:taskId" element={<UpdateTaskSubmission />} />
                </>
              )}

              {/* Task Counter end-user routes — Ops Work Log only */}
              {isOpsWorkLog ? (
                <>
                  <Route path="/task-counter-submission" element={<TaskCounterSubmissionList />} />
                  <Route path="/task-counter-screen" element={<TaskCounterScreen />} />
                </>
              ) : (
                <>
                  <Route path="/task-counter-submission" element={<Navigate to="/task-submission" replace />} />
                  <Route path="/task-counter-screen" element={<Navigate to="/task-submission" replace />} />
                </>
              )}
            </>
          )}
        </>
      ) : <Route path="*" element={<Navigate to="/" replace />} />}

      {isRoleAdmin || isRoleSupperAdmin ? (
        <>
          <Route path="/user-management" element={<UserManagement />} />
          <Route path="/create-new-user" element={<CreateNewUser />} />
          <Route path="/update-user/:userId" element={<UpdateUserDetail />} />
          <Route path="/company-management" element={<CompanyManagement />} />
          <Route path="/create-organization" element={<CreateNewCompany />} />
          <Route
            path="/update-organization/:companyId"
            element={<UpdateCompanyInfo />}
          />
          <Route path="/create-job-title" element={<CreateNewJobTitle />} />
          <Route path="/update-job/:jobId" element={<UpdateJobTitle />} />
          <Route
            path="/tasks-list-management"
            element={<TasksListManagement />}
          />
          <Route path="/create-new-template" element={<CreateNewTemplate />} />
          <Route
            path="/update-task-template/:id"
            element={<UpdateTemplateDetail />}
          />
          <Route path="/task-counter" element={<TaskCounterList />} />
          <Route
            path="/create-task-counter-template"
            element={<CreateTaskCounterTemplate />}
          />
          <Route
            path="/update-task-counter-template/:id"
            element={<UpdateTaskCounterTemplate />}
          />
        </>
      ) : (
        <Route path="*" element={<PageNotFound />} />
      )}
      {isRoleSupperAdmin ? (
        <>
          <Route path="/create-company" element={<CreateParentCompany />} />
          <Route
            path="/update-company/:id"
            element={<UpdateParentCompany />}
          />
        </>
      ) : null}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};
